import React, { Component, Children, createRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getDomPath } from '@thd-olt-functional/utils';
import { Carousel } from '@thd-olt-component-react/carousel';
import { TabGroupContext } from './TabGroupContext';
import { UUID } from '../../utils';
import { publisher } from '../../publisher';
import '../Tab.style.scss';

const SPACE_STANDARD = 10;

class TabGroup extends Component {
  static getChildByType = (tab, type) => tab.props.children
    .find((child) => child.type.displayName === type);

  state = {
    activeTab: this.props.activeIndex,//eslint-disable-line
    id: UUID()
  };

  tabLineRef = React.createRef();

  tabRefs = [];

  nodeRef = createRef();

  componentDidMount = () => {
    const { activeTab } = this.state;
    const { gridStyle } = this.props;
    if (this.tabRefs && this.tabRefs.length > 0) {
      if (!gridStyle) {
        this.updateTabsLine({ tab: this.tabRefs[activeTab] });
      }
    }
  };

  componentDidUpdate = (prevProps, prevState, snapshot) => {
    const { activeTab, id } = this.state;
    if (activeTab !== prevState.activeTab) {
      publisher('update', { activeTabIndex: activeTab, id });
    }
  };

  onMouseEnterTab = (tab) => {
    const { currentTarget } = tab;
    const { activeTab } = this.state;
    if (activeTab === parseInt(currentTarget.value, 10)) {
      this.updateTabsLine({ tab: currentTarget, expand: true });
    }
  };

  onMouseLeaveTab = (tab) => {
    const { currentTarget } = tab;
    const { activeTab } = this.state;
    if (activeTab === parseInt(currentTarget.value, 10)) {
      this.updateTabsLine({ tab: currentTarget, expand: false });
    }
  };

  onClickTab = (tab, clickCB) => {
    if (typeof clickCB === 'function') {
      clickCB(tab);
    }
    const { currentTarget } = tab;
    const { onChange, gridStyle } = this.props;
    this.setState((prevState) => {
      const { id } = prevState;
      const nextActiveTab = parseInt(currentTarget.value, 10);
      let text = '';
      if (this.tabRefs && this.tabRefs.length > 0) {
        text = this.tabRefs[nextActiveTab].innerText;
      }
      if (!gridStyle) {
        this.updateTabsLine({ tab: currentTarget, expand: true });
      }
      const activeTabIndex = nextActiveTab;
      const path = getDomPath(this.nodeRef);

      publisher('click', { activeTabIndex, text, path, id });
      if (onChange) {
        onChange({ index: activeTabIndex });
      }
      return { activeTab: nextActiveTab };
    });
  };

  updateTabsLine = ({ tab, expand = false }) => {
    const { offsetLeft, offsetWidth } = tab;
    const line = this.tabLineRef.current;
    const widthOffset = expand ? 0 : 2 * SPACE_STANDARD;
    const leftOffset = expand ? SPACE_STANDARD : 0;
    line.style.width = `${offsetWidth - widthOffset}px`;
    line.style.left = `${offsetLeft - leftOffset}px`;
  };

  titleMapping = () => {
    const { activeTab, id } = this.state;
    const { children } = this.props;
    return (Children.map(children, (tab, index) => {
      const tabTitle = tab ? TabGroup.getChildByType(tab, 'TabTitle') : '';
      const titleId = `${id}-${index + 1}`;
      let cloneElement;
      if (tabTitle) {
        cloneElement = React.cloneElement(tabTitle, {
          key: `key-${titleId}`,
          id: `tab-${titleId}`,
          checked: activeTab === index,
          name: id,
          value: index,
          onClick: (event) => { this.onClickTab(event, tabTitle.props.onClick); },
          onMouseEnterTab: this.onMouseEnterTab,
          onMouseLeaveTab: this.onMouseLeaveTab,
          captureRef: (node) => {
            this.tabRefs[index] = node;
          }
        });
      }
      return cloneElement;
    }));
  };

  contentMapping = () => {
    const { activeTab, id } = this.state;
    const { children, suppressInactiveTabs } = this.props;
    let renderedChildren = children;
    if (suppressInactiveTabs) {
      renderedChildren = children.filter((child, index) => index === activeTab);
    }
    return (Children.map(renderedChildren, (tab, index) => {
      const tabContent = tab ? TabGroup.getChildByType(tab, 'TabContent') : '';
      const contentId = `${id}-${index + 1}`;
      let cloneElement;
      if (tabContent) {
        cloneElement = React.cloneElement(tabContent, {
          key: `key-item-${contentId}`,
          id: `tab-item-${contentId}`,
          checked: !!suppressInactiveTabs || activeTab === index
        });
      }
      return cloneElement;
    }));
  };

  render() {
    const { className, gridStyle } = this.props;
    const { id } = this.state;
    const tabGroupClasses = classNames('tab-group',
      className,
      { 'tab-group--grid': gridStyle },
    );
    const tabGroupWrapperClasses = classNames(
      'tab-group__wrapper',
    );
    const tabContentClasses = classNames(
      'tab-content',
      { 'tab-content--grid': gridStyle }
    );
    const tabLineClasses = classNames(
      'tab-group__line',
      'tab-group__ease',
      { 'tab-group__line--no-line': gridStyle }
    );
    return (
      <TabGroupContext.Provider value={{ id }}>
        <div className={tabGroupClasses} ref={this.nodeRef}>
          <div className={tabGroupWrapperClasses}>
            {gridStyle
            && (
              <Carousel
                fixedItem
                nonUniformItem
                fade
                multiItem
                useGradientArrow
                itemClass="tab-item"
                carouselParentRef={this.nodeRef.current}
              >
                {this.titleMapping()}
              </Carousel>
            )}
            {!gridStyle && this.titleMapping()}
            <div ref={this.tabLineRef} className={tabLineClasses} />
          </div>
          <div className={tabContentClasses}>
            {this.contentMapping()}
          </div>
        </div>
      </TabGroupContext.Provider>
    );
  }
}
TabGroup.contextType = TabGroupContext;

TabGroup.displayName = 'TabGroup';

TabGroup.propTypes = {
  activeIndex: PropTypes.number,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func,
  gridStyle: PropTypes.bool,
  suppressInactiveTabs: PropTypes.bool
};

TabGroup.defaultProps = {
  activeIndex: 0,
  className: null,
  onChange: null,
  gridStyle: false,
  suppressInactiveTabs: false
};

export default TabGroup;
